import {Middleware} from '@reduxjs/toolkit'

import {includes, map} from '@shared/helpers/collection'
import {flow} from '@shared/helpers/function'
import {cloneDeep, eq, isEmpty} from '@shared/helpers/lang'
import {values, deepMerge, deepMergeWithValid} from '@shared/helpers/object'
import {qs} from '@shared/helpers/queryString'

import {getChargePointBalanceBenefit} from '@web/actions/pointPayment'
import {getEasyAccounts} from '@web/actions/priorityPaymentMethod'
import {PAYMONEY_RESULT_TYPE, PayMoneyUserStatusValueType, PayMoneyUserValueStatus} from '@web/core/constants'

import {initPaymentMethodManagement} from '../../actions'
import {Card, ChargePointLimitResult, MemberInfoResult} from '../../apis/interfaces'
import {State} from '../../interfaces'

/** 깊은 복제된 액션 객체를 반환하고, demo 에 의해 조작되고 있음을 경고합니다. */
const cloneAction = <A>(action: A): A => {
    // eslint-disable-next-line no-console
    console.warn('demoMiddleware 가 작동중입니다.', action)
    return cloneDeep(action)
}

/** 유효한 페이머니 유저 상태값인지 확인 */
const validPayMoneyUserStatus = (value: unknown): value is PayMoneyUserStatusValueType =>
    !isEmpty(value) && includes(value, values(PayMoneyUserValueStatus))

/**
 * 목록 내 카드 아이템의 사용가능 여부를 false 로 변경
 */
const makeInvalidCardList = map((card: Card) => ({...card, valid: false}))

/**
 * 테스트 케이스를 제공하기 위한 middleware
 * - 테스트용 query string 을 확인한 후 변형된 액션으로 반환합니다.
 */
const demoMiddleware: Middleware<object, State> = () => (next) => (action) => {
    const nextAction = cloneAction(action)
    const getQuery = (key: string): string | undefined => qs.parse(window.location.search)[key]
    /** 해당 키를 가진 값이 'Y' 와의 일치여부 비교 */
    const compareQueryWithY: (key: string) => boolean = flow(flow(getQuery, eq('Y')))
    /** 간편계좌 전체 점검중 케이스 */
    const isBankUnderMaintenance = compareQueryWithY('isBankUnderMaintenance')
    /** 은행 전체 점검중 케이스 */
    const isAllBankUnderMaintenance = compareQueryWithY('isAllBankUnderMaintenance')
    /** 페이포인트 중 적립포인트 잔액조회 실패 케이스 */
    const isRewardPointCheckFailed = compareQueryWithY('isRewardPointCheckFailed')
    /** 페이포인트 충전포인트 잔액조회 실패 케이스 */
    const isChargePointCheckFailed = compareQueryWithY('isChargePointCheckFailed')
    /** 페이포인트 충전포인트 잔액조회 재조회 가능 케이스 */
    const chargePointCheckRetryAvailable = compareQueryWithY('chargePointCheckRetryAvailable')
    /** 페이포인트 중 적립포인트 잔액조회 실패 케이스 */
    const payMoneyUserStatus = getQuery('payMoneyUserStatus')
    /** 간편카드 전체 점검중 케이스 */
    const isCardUnderMaintenance = compareQueryWithY('isCardUnderMaintenance')
    /** 카드 목록 조회 실패 케이스 */
    const isFailedGetCardList = compareQueryWithY('isFailedGetCardList')
    /** 계좌 목록 조회 실패 케이스 */
    const isFailedGetAccountList = compareQueryWithY('isFailedGetAccountList')
    /** 페이머니 회원조회 실패 케이스 */
    const isPaymoneyMemberCheckFailed = compareQueryWithY('isPaymoneyMemberCheckFailed')
    /** 페이머니 한도조회 실패 케이스 */
    const isPaymoneyLimitFailed = compareQueryWithY('isPaymoneyLimitFailed')
    /** 페이머니 지급가능 잔액과 보유 잔액 상이 케이스  */
    const hasDifferentPaymoneyAmount = compareQueryWithY('hasDifferentPaymoneyAmount')
    /** 기존 결제수단 무시 */
    const shouldIgnoreRecentPaymentInfo = compareQueryWithY('ignoreRecentPaymentInfo')

    if (isBankUnderMaintenance) {
        const acctListMapper = <AC>(acct: AC) => ({
            ...acct,
            isPayable: false,
        })
        // 정기결제 초기 조회 시 간편계좌의 사용가능 여부 falsify
        if (initPaymentMethodManagement.fulfilled.match(nextAction)) {
            nextAction.payload.response = deepMergeWithValid(
                {
                    accountResult: {
                        acctList: nextAction.payload.response.accountResult.acctList?.map(acctListMapper),
                        resultCode: '3106',
                    },
                },
                nextAction.payload.response,
            )
        }

        // 간편계좌 목록 조회 시 간편계좌의 사용가능 여부 falsify
        if (getEasyAccounts.fulfilled.match(nextAction)) {
            nextAction.payload = deepMergeWithValid(
                {
                    acctList: nextAction.payload.acctList.map(acctListMapper),
                    resultCode: '3106',
                },
                nextAction.payload,
            )
        }
    }

    if (isAllBankUnderMaintenance) {
        if (initPaymentMethodManagement.fulfilled.match(nextAction)) {
            nextAction.payload.response = deepMergeWithValid(
                {
                    accountResult: {
                        inspection: {
                            status: 'COMMON_INSPECT',
                            startDate: '',
                            startTime: '23:45',
                            endDate: '',
                            endTime: '00:10',
                            message: '',
                        },
                    },
                },
                nextAction.payload.response,
            )
        }
    }

    // 사용자 정보 조회 액션 관련 조작
    if (validPayMoneyUserStatus(payMoneyUserStatus)) {
        if (initPaymentMethodManagement.fulfilled.match(nextAction)) {
            nextAction.payload.response.memberInfoResult.version = payMoneyUserStatus
        }
    }

    // 적립포인트 조회 액션 관련 조작
    if (isRewardPointCheckFailed) {
        if (initPaymentMethodManagement.fulfilled.match(nextAction)) {
            nextAction.payload.response = deepMergeWithValid(
                {
                    paymoneyAndPointResult: {
                        reward: {
                            balanceAmount: 0,
                            cashableBalanceAmount: 0,
                            resultType: PAYMONEY_RESULT_TYPE.FAILED_RETRY_AVAILABLE,
                        },
                    },
                },
                nextAction.payload.response,
            )
        }
    }

    // 충전포인트 조회 실패 : 초기 데이터 조회 액션 실행 시
    if (isChargePointCheckFailed) {
        if (initPaymentMethodManagement.fulfilled.match(nextAction)) {
            nextAction.payload.response = deepMergeWithValid(
                {
                    paymoneyAndPointResult: {
                        paymoney: {
                            balanceAmount: 0,
                            cashableBalanceAmount: 0,
                            resultType: chargePointCheckRetryAvailable ? 'FAILED_RETRY_AVAILABLE' : 'FAILED',
                        },
                    },
                },
                nextAction.payload.response,
            )
        }
    }

    // 간편게좌 정보 조회 액션 관련 조작
    if (initPaymentMethodManagement.fulfilled.match(nextAction)) {
        if (isBankUnderMaintenance) {
            nextAction.payload.response = deepMergeWithValid(
                {
                    accountResult: {
                        acctList: nextAction.payload.response.accountResult.acctList.map((acct) => ({
                            ...acct,
                            isPayable: false,
                        })),
                        resultCode: '3106',
                    },
                },
                nextAction.payload.response,
            )
        }
    }

    // 간편카드 점검중 demo
    if (isCardUnderMaintenance) {
        if (initPaymentMethodManagement.fulfilled.match(nextAction)) {
            nextAction.payload.response = deepMergeWithValid(
                {
                    cardResult: {
                        result: {
                            cardList: makeInvalidCardList(nextAction.payload.response.cardResult.result.cardList),
                        },
                    },
                },
                nextAction.payload.response,
            )
        }
    }

    if (isFailedGetCardList) {
        const CARD_RESULT = {
            result: {cardList: []},
            code: '3000',
        }

        if (initPaymentMethodManagement.fulfilled.match(nextAction)) {
            nextAction.payload.response = deepMergeWithValid(
                {
                    cardResult: CARD_RESULT,
                },
                nextAction.payload.response,
            )
        }
    }

    if (isFailedGetAccountList) {
        const ACCOUNT_RESULT = {
            acctList: [],
            resultCode: '4000',
        }

        if (initPaymentMethodManagement.fulfilled.match(nextAction)) {
            nextAction.payload.response = deepMergeWithValid(
                {
                    accountResult: ACCOUNT_RESULT,
                },
                nextAction.payload.response,
            )
        }

        if (getEasyAccounts.fulfilled.match(nextAction)) {
            nextAction.payload = deepMergeWithValid(ACCOUNT_RESULT, nextAction.payload)
        }
    }

    if (isPaymoneyMemberCheckFailed) {
        if (initPaymentMethodManagement.fulfilled.match(nextAction)) {
            const memberInfoResult = {resultType: 'FAILED'} as MemberInfoResult

            nextAction.payload.response = {
                ...nextAction.payload.response,
                memberInfoResult,
            }
        }
    }

    if (isPaymoneyLimitFailed) {
        if (initPaymentMethodManagement.fulfilled.match(nextAction)) {
            const chargePointLimitResult = {resultType: 'FAILED'} as ChargePointLimitResult

            nextAction.payload.response = {
                ...nextAction.payload.response,
                chargePointLimitResult,
            }
        }
    }

    if (hasDifferentPaymoneyAmount) {
        if (getChargePointBalanceBenefit.fulfilled.match(nextAction)) {
            const prevState = nextAction.payload
            const chargePointResult = {
                ...prevState,
                totalAmt: prevState.availableAmount + 100,
            }

            nextAction.payload = {
                ...nextAction.payload,
                ...chargePointResult,
            }
        }
    }

    if (shouldIgnoreRecentPaymentInfo) {
        if (initPaymentMethodManagement.fulfilled.match(nextAction)) {
            nextAction.payload.response = deepMerge(
                {
                    bookingOrderSheetResult: {
                        recentPayInfo: null,
                        regPgs: [],
                    },
                },
                nextAction.payload.response,
            )
        }
    }

    next(nextAction)
}

/** 주문서 관련  */
export default demoMiddleware
